import React, { useEffect, useState } from "react";
import {
    Box,
    Typography,
    Badge,
    Popover,
    List,
    ListItem,
    ListItemText,
    Divider,
    IconButton,
    Button,
    Chip,
    LinearProgress,
    Tooltip,
    CircularProgress,
    Menu,
    MenuItem,
    useTheme,
    alpha
} from "@mui/material";
import {
    Notifications as NotificationsIcon,
    Circle,
    CalendarToday,
    Person,
    AccessTime,
    FilterList,
    CheckCircle,
    PriorityHigh,
    MoreVert,
    ArrowForward,
    Warning,
    Task,
    Circle as CircleIcon,
    KeyboardArrowRight
} from "@mui/icons-material";
import { getTodoTask } from "../../../services/todoTask";
import moment from "moment";
import { useNavigate } from "react-router-dom";

const Notification = ({ userId, roleName }) => {
    const theme = useTheme();
    const navigate = useNavigate();
    const [anchorEl, setAnchorEl] = useState(null);
    const [pendingTasks, setPendingTasks] = useState([]);
    const [pendingCount, setPendingCount] = useState(0);
    const [completedCount, setCompletionCount] = useState(0);
    const [loading, setLoading] = useState(false);
    const [filter, setFilter] = useState("all");
    const [filterAnchorEl, setFilterAnchorEl] = useState(null);

    const openPopover = Boolean(anchorEl);
    const openFilterMenu = Boolean(filterAnchorEl);

    // Theme-aware color functions


    const getStatusColor = (status) => {
        switch (status) {
            case 0: // Pending
                return theme.palette.warning.main;
            case 1: // Completed
                return theme.palette.success.main;
            case 2: // Canceled
                return theme.palette.error.main;
            default:
                return theme.palette.text.secondary;
        }
    };

    const getTimeStatus = (date) => {
        const taskDate = moment(date);
        const today = moment().startOf('day');
        const diffDays = taskDate.diff(today, 'days');

        if (diffDays < 0) {
            return {
                text: 'Overdue',
                color: theme.palette.error.main,
                icon: <Warning fontSize="small" />
            };
        }
        if (diffDays === 0) {
            return {
                text: 'Today',
                color: theme.palette.warning.main,
                icon: <AccessTime fontSize="small" />
            };
        }
        if (diffDays <= 2) {
            return {
                text: 'Soon',
                color: theme.palette.info.main,
                icon: <AccessTime fontSize="small" />
            };
        }
        return {
            text: 'Upcoming',
            color: theme.palette.text.secondary,
            icon: <CalendarToday fontSize="small" />
        };
    };

    const fetchPendingTasks = async () => {
        if (!userId) return;

        setLoading(true);
        try {
            const isAdmin = roleName === "Admin";
            const params = {
                status: 0,
                limit: 100,
                page: 1,
            };

            if (!isAdmin) {
                params.tab = "assigned";
                params.userId = userId;
            }

            const res = await getTodoTask(params);
            if (res.data && res.data.data) {
                const tasks = res.data.data;
                setPendingTasks(tasks);
                // Use pendingCount from API if available, otherwise count
                setPendingCount(res.data.pendingCount || tasks.length);
                setCompletionCount(res.data.completedCount || tasks.length);
            }
        } catch (error) {
            console.error("Error fetching pending tasks:", error);
        } finally {
            setLoading(false);
        }
    };

    const handleBellClick = (event) => {
        setAnchorEl(event.currentTarget);
        fetchPendingTasks();
    };

    const handleBellClose = () => {
        setAnchorEl(null);
    };

    const handleFilterClick = (event) => {
        setFilterAnchorEl(event.currentTarget);
    };

    const handleFilterClose = (selectedFilter) => {
        if (selectedFilter) {
            setFilter(selectedFilter);
        }
        setFilterAnchorEl(null);
    };

    const getFilteredTasks = () => {
        const now = moment();
        switch (filter) {

            case "today":
                return pendingTasks.filter(task =>
                    moment(task.completionDate).isSame(now, 'day')
                );
            case "overdue":
                return pendingTasks.filter(task =>
                    moment(task.completionDate).isBefore(now, 'day')
                );
            default:
                return pendingTasks;
        }
    };

    const handleTaskClick = (task) => {
        // Navigate to task or open modal
        console.log("Task clicked:", task);
        // handleBellClose();
    };

    const handleMarkAllAsRead = () => {
        // Implement mark all as read
        console.log("Mark all as read");
    };

    const handleViewAll = () => {
        // Navigate to tasks page
        navigate("/home/todoTask");
        handleBellClose();
    };

    useEffect(() => {
        if (userId && roleName) {
            fetchPendingTasks();
            // const interval = setInterval(fetchPendingTasks, 3000000);
            // return () => clearInterval(interval);
        }
    }, [userId, roleName]);

    const filteredTasks = getFilteredTasks();

    const todayCount = pendingTasks.filter(t =>
        moment(t.completionDate).isSame(moment(), 'day')
    ).length;
    const overdueCount = pendingTasks.filter(t =>
        moment(t.completionDate).isBefore(moment(), 'day')
    ).length;

    return (
        <>
            <Tooltip title="Notifications">
                <IconButton
                    onClick={handleBellClick}
                    sx={{
                        position: 'relative',
                        '&::after': pendingCount > 0 ? {
                            content: '""',
                            position: 'absolute',
                            top: 8,
                            right: 8,
                            width: 8,
                            height: 8,
                            borderRadius: '50%',
                            bgcolor: theme.palette.error.main,
                            animation: 'pulse 2s infinite'
                        } : {}
                    }}
                >
                    <Badge
                        badgeContent={pendingCount}
                        color="error"
                        max={99}
                        sx={{
                            '& .MuiBadge-badge': {
                                fontSize: '0.65rem',
                                height: 16,
                                minWidth: 16,
                                fontWeight: 600,
                                borderRadius: '50%'
                            }
                        }}
                    >
                        <NotificationsIcon />
                    </Badge>
                </IconButton>
            </Tooltip>

            <Popover
                open={openPopover}
                anchorEl={anchorEl}
                onClose={handleBellClose}
                anchorOrigin={{
                    vertical: 'bottom',
                    horizontal: 'right',
                }}
                transformOrigin={{
                    vertical: 'top',
                    horizontal: 'right',
                }}
                PaperProps={{
                    sx: {
                        width: 380,
                        maxHeight: 500,
                        borderRadius: 2,
                        boxShadow: theme.shadows[8],
                        overflow: 'hidden',
                        border: `1px solid ${theme.palette.divider}`
                    }
                }}
            >
                {/* Header */}
                <Box sx={{
                    p: 2,
                    background: theme.palette.mode === 'dark'
                        ? 'linear-gradient(135deg, #424242 0%, #616161 100%)'
                        : 'linear-gradient(135deg, #008080 0%, #055253 100%)',
                    color: theme.palette.common.white
                }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                            <NotificationsIcon sx={{ fontSize: 24 }} />
                            <Typography variant="h6" sx={{ fontWeight: 600 }}>
                                Pending Tasks
                            </Typography>
                        </Box>
                        <Box sx={{ display: 'flex', gap: 0.5 }}>
                            <Tooltip title="Filter">
                                <IconButton
                                    size="small"
                                    sx={{ color: 'inherit', opacity: 0.9, '&:hover': { opacity: 1 } }}
                                    onClick={handleFilterClick}
                                >
                                    <FilterList />
                                </IconButton>
                            </Tooltip>
                            <Tooltip title="Close">
                                <IconButton
                                    size="small"
                                    sx={{ color: 'inherit', opacity: 0.9, '&:hover': { opacity: 1 } }}
                                    onClick={handleBellClose}
                                >
                                    <ArrowForward sx={{ transform: 'rotate(90deg)' }} />
                                </IconButton>
                            </Tooltip>
                        </Box>
                    </Box>

                    {/* Stats */}
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 3, mt: 2 }}>
                        <Box>
                            <Typography variant="h4" sx={{ fontWeight: 700 }}>
                                {pendingCount}
                            </Typography>
                            <Typography variant="caption" sx={{ opacity: 0.9 }}>
                                Total Pending
                            </Typography>
                        </Box>
                        <Divider orientation="vertical" flexItem sx={{ bgcolor: 'rgba(255,255,255,0.3)' }} />
                        <Box>
                            <Typography variant="h4" sx={{
                                fontWeight: 700,
                                color: theme.palette.error.light
                            }}>
                                {overdueCount}
                            </Typography>
                            <Typography variant="caption" sx={{ opacity: 0.9 }}>
                                Overdue
                            </Typography>
                        </Box>
                        <Box>
                            <Typography variant="h4" sx={{
                                fontWeight: 700,
                                color: theme.palette.warning.light
                            }}>
                                {todayCount}
                            </Typography>
                            <Typography variant="caption" sx={{ opacity: 0.9 }}>
                                Due Today
                            </Typography>
                        </Box>
                    </Box>
                </Box>

                {/* Filter Menu */}
                <Menu
                    anchorEl={filterAnchorEl}
                    open={openFilterMenu}
                    onClose={() => handleFilterClose()}
                    PaperProps={{
                        sx: {
                            mt: 1,
                            minWidth: 200,
                            borderRadius: 1
                        }
                    }}
                >
                    <MenuItem
                        onClick={() => handleFilterClose("all")}
                        selected={filter === "all"}
                        sx={{ py: 1 }}
                    >
                        <Task sx={{ mr: 1.5, fontSize: 20, color: theme.palette.primary.main }} />
                        All Tasks ({pendingCount})
                    </MenuItem>
                    <MenuItem
                        onClick={() => handleFilterClose("today")}
                        selected={filter === "today"}
                        sx={{ py: 1 }}
                    >
                        <CalendarToday sx={{ mr: 1.5, fontSize: 20, color: theme.palette.warning.main }} />
                        Due Today ({todayCount})
                    </MenuItem>
                    <MenuItem
                        onClick={() => handleFilterClose("overdue")}
                        selected={filter === "overdue"}
                        sx={{ py: 1 }}
                    >
                        <Warning sx={{ mr: 1.5, fontSize: 20, color: theme.palette.error.main }} />
                        Overdue ({overdueCount})
                    </MenuItem>
                </Menu>

                {/* Progress Indicator */}
                {/* <Box sx={{
                    px: 2,
                    py: 1.5,
                    bgcolor: theme.palette.mode === 'dark'
                        ? alpha(theme.palette.background.paper, 0.5)
                        : theme.palette.grey[50],
                    borderBottom: `1px solid ${theme.palette.divider}`
                }}>
                    {/* <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                        <Typography variant="caption" color="text.secondary">
                            Task Completion Priority
                        </Typography>
                        <Typography variant="caption" color="text.secondary" fontWeight={500}>
                            {highPriorityCount} of {pendingCount} high priority
                        </Typography>
                    </Box> */}
                {/* <LinearProgress
                        variant="determinate"
                        value={(highPriorityCount / Math.max(pendingCount, 1)) * 100}
                        sx={{
                            height: 6,
                            borderRadius: 3,
                            bgcolor: theme.palette.mode === 'dark' ? '#424242' : '#e0e0e0',
                            '& .MuiLinearProgress-bar': {
                                bgcolor: theme.palette.error.main,
                                borderRadius: 3
                            }
                        }}
                    /> */}
                {/* </Box> */}

                {/* Tasks List */}
                <List sx={{
                    p: 0,
                    maxHeight: 300,
                    overflow: 'auto',
                    bgcolor: theme.palette.background.paper
                }}>
                    {loading ? (
                        <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
                            <CircularProgress size={24} />
                        </Box>
                    ) : filteredTasks.length > 0 ? (

                        filteredTasks.map((task, index) => {
                            const timeStatus = getTimeStatus(task.completionDate);
                            const isOverdue = moment(task.completionDate).isBefore(moment(), 'day');

                            return (
                                <React.Fragment key={task._id}>
                                    <ListItem
                                        onClick={() => handleTaskClick(task)}
                                        sx={{
                                            py: 1,
                                            px: 2,
                                            flexDirection: 'column',
                                            gap: 0.5,
                                            cursor: 'pointer',
                                            '&:hover': {
                                                bgcolor: theme.palette.action.hover
                                            },
                                            bgcolor: isOverdue
                                                ? alpha(theme.palette.error.main, 0.04)
                                                : 'transparent',
                                            transition: 'background-color 0.2s ease'
                                        }}
                                    >
                                        <Box sx={{ width: '100%' }}>
                                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 0.5 }}>
                                                <Typography variant="body2" sx={{
                                                    fontWeight: 600,
                                                    color: theme.palette.text.primary,
                                                    overflow: 'hidden',
                                                    textOverflow: 'ellipsis',
                                                    whiteSpace: 'nowrap',
                                                    maxWidth: '220px',
                                                }}>
                                                    {task.title}
                                                </Typography>
                                                <Typography variant="caption" sx={{
                                                    color: timeStatus.color,
                                                    fontWeight: 600,
                                                    fontSize: '0.7rem',
                                                    whiteSpace: 'nowrap',
                                                    ml: 1
                                                }}>
                                                    {timeStatus.text === 'Today' ? 'Today' : moment(task.completionDate).format("MMM DD")}
                                                </Typography>
                                            </Box>

                                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                                <Typography variant="caption" sx={{
                                                    color: theme.palette.text.secondary,
                                                    overflow: 'hidden',
                                                    textOverflow: 'ellipsis',
                                                    whiteSpace: 'nowrap',
                                                    maxWidth: '180px'
                                                }}>
                                                    {task.project_info?.name || 'No Project'}
                                                </Typography>

                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                                    <Person sx={{ fontSize: 12, color: theme.palette.text.secondary }} />
                                                    <Typography variant="caption" color="text.secondary">
                                                        {roleName === "Admin"
                                                            ? (task.assignedTo_info?.firstName || 'Unassigned')
                                                            : (task.createdBy_info?.firstName || 'Unknown')
                                                        }
                                                    </Typography>
                                                </Box>
                                            </Box>
                                        </Box>
                                    </ListItem>
                                    {index < filteredTasks.length - 1 && (
                                        <Divider component="li" sx={{ mx: 2, opacity: 0.5 }} />
                                    )}
                                </React.Fragment>
                            );
                        })
                    ) : (
                        <ListItem sx={{ py: 4, flexDirection: 'column', textAlign: 'center' }}>
                            <CheckCircle sx={{
                                fontSize: 40,
                                color: theme.palette.text.disabled,
                                mb: 1
                            }} />
                            <Typography variant="body2" color="text.secondary" gutterBottom>
                                No pending tasks
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                                {filter === "all"
                                    ? "You're all caught up!"
                                    : `No ${filter} tasks found`}
                            </Typography>
                        </ListItem>
                    )}
                </List>

                {/* Footer Actions */}
                <Box sx={{
                    p: 1.5,
                    borderTop: `1px solid ${theme.palette.divider}`,
                    display: 'flex',
                    justifyContent: 'space-between',
                    bgcolor: theme.palette.mode === 'dark'
                        ? alpha(theme.palette.background.paper, 0.5)
                        : theme.palette.grey[50]
                }}>
                    {/* <Button
                        size="small"
                        onClick={handleMarkAllAsRead}
                        disabled={pendingCount === 0}
                        sx={{ color: theme.palette.text.secondary }}
                    >
                        Clear All
                    </Button> */}
                    <Button
                        size="small"
                        endIcon={<KeyboardArrowRight />}
                        onClick={handleViewAll}
                        sx={{ color: theme.palette.primary.main }}
                    >
                        View All Tasks
                    </Button>
                </Box>
            </Popover >

            {/* Add keyframes for pulse animation */}
            <style>{`
                @keyframes pulse {
                    0% {
                        transform: scale(1);
                        opacity: 1;
                    }
                    50% {
                        transform: scale(1.2);
                        opacity: 0.7;
                    }
                    100% {
                        transform: scale(1);
                        opacity: 1;
                    }
                }
            `}</style>
        </>
    );
};

export default Notification;